import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  InputBase,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { ProgramArea, WorkflowDueType } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";

const CREATE_CUSTOM_WORK_ITEM = gql(`
  mutation CreateCustomWorkItem($input: CreateCustomWorkItemInput!) {
    createCustomWorkItem(input: $input)
  }
`);

const TENANT_OPTIONS = gql(`
  query NewWorkTenantOptions($search: String, $pageSize: Int) {
    tenants(search: $search, pageSize: $pageSize) {
      items {
        id
        name
      }
    }
  }
`);

type TenantOption = { id: string; name: string };

type NewWorkDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

/**
 * Compose state for a new custom deliverable: the field values, the debounced
 * tenant lookup that backs the picker, and the create call. Split from the
 * dialog so the component below is layout.
 */
function useNewWorkForm({ open, onClose, onCreated }: NewWorkDialogProps) {
  const alerts = useAlerts();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tenant, setTenant] = useState<TenantOption | null>(null);
  const [tenantSearch, setTenantSearch] = useState("");
  const [programArea, setProgramArea] = useState<ProgramArea | "">("");
  const [dueAt, setDueAt] = useState<DateTime | null>(null);
  const [dueType, setDueType] = useState<WorkflowDueType>(WorkflowDueType.Internal);

  const [loadTenants, { data: tenantData, loading: tenantsLoading }] = useLazyQuery(TENANT_OPTIONS);
  const [createWorkItem, { loading: creating }] = useMutation(CREATE_CUSTOM_WORK_ITEM);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handle = setTimeout(() => {
      loadTenants({ variables: { search: tenantSearch, pageSize: 20 } });
    }, 250);
    return () => clearTimeout(handle);
  }, [open, tenantSearch, loadTenants]);

  const tenantOptions: TenantOption[] = useMemo(
    () => tenantData?.tenants.items ?? [],
    [tenantData]
  );

  const canSubmit = title.trim().length > 0 && tenant !== null && programArea !== "";

  const handleCreate = useCallback(async () => {
    if (!canSubmit || !tenant || !programArea) {
      return;
    }
    try {
      await createWorkItem({
        variables: {
          input: {
            title: title.trim(),
            description: description.trim() || undefined,
            tenantId: tenant.id,
            programArea,
            dueAt: dueAt?.toISO() ?? undefined,
            dueType: dueAt ? dueType : undefined,
          },
        },
      });
      alerts.success("Work item created");
      onCreated();
      onClose();
    } catch (error) {
      alerts.error("Failed to create work item", error as Error);
    }
  }, [
    canSubmit,
    tenant,
    programArea,
    createWorkItem,
    title,
    description,
    dueAt,
    dueType,
    alerts,
    onCreated,
    onClose,
  ]);

  return {
    title,
    setTitle,
    description,
    setDescription,
    tenant,
    setTenant,
    setTenantSearch,
    programArea,
    setProgramArea,
    dueAt,
    setDueAt,
    dueType,
    setDueType,
    tenantOptions,
    tenantsLoading,
    canSubmit,
    creating,
    handleCreate,
  };
}

type WorkPropertiesBarProps = Pick<
  ReturnType<typeof useNewWorkForm>,
  | "tenant"
  | "setTenant"
  | "setTenantSearch"
  | "programArea"
  | "setProgramArea"
  | "dueAt"
  | "setDueAt"
  | "dueType"
  | "setDueType"
  | "tenantOptions"
  | "tenantsLoading"
>;

/** The required properties, gathered in one subtle toolbar box above the actions. */
function WorkPropertiesBar({
  tenant,
  setTenant,
  setTenantSearch,
  programArea,
  setProgramArea,
  dueAt,
  setDueAt,
  dueType,
  setDueType,
  tenantOptions,
  tenantsLoading,
}: WorkPropertiesBarProps) {
  return (
    <Box
      sx={(theme) => ({
        px: 1.25,
        py: 1.25,
        borderRadius: 2,
        backgroundColor: theme.palette.surfaceSubtle,
        border: `1px solid ${theme.palette.divider}`,
      })}
    >
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Autocomplete
          size="small"
          sx={{ minWidth: 220 }}
          options={tenantOptions}
          value={tenant}
          loading={tenantsLoading}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_e, value) => setTenant(value)}
          onInputChange={(_e, value) => setTenantSearch(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Tenant"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {tenantsLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <TextField
          select
          required
          size="small"
          label="Program area"
          sx={{ minWidth: 160 }}
          value={programArea}
          onChange={(e) => setProgramArea(e.target.value as ProgramArea)}
        >
          {Object.values(ProgramArea).map((area) => (
            <MenuItem key={area} value={area}>
              {area}
            </MenuItem>
          ))}
        </TextField>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DatePicker
            label="Due date"
            value={dueAt}
            onChange={setDueAt}
            slotProps={{ textField: { size: "small", sx: { minWidth: 160 } } }}
          />
        </LocalizationProvider>
        {dueAt && (
          <TextField
            select
            size="small"
            label="Due kind"
            sx={{ minWidth: 140 }}
            value={dueType}
            onChange={(e) => setDueType(e.target.value as WorkflowDueType)}
          >
            <MenuItem value={WorkflowDueType.Internal}>Internal</MenuItem>
            <MenuItem value={WorkflowDueType.Regulatory}>Regulatory</MenuItem>
          </TextField>
        )}
      </Stack>
    </Box>
  );
}

/**
 * The "+ New work" compose for a flat Custom Deliverable, styled after the
 * Tasks dialog and Linear's issue compose: a borderless title up top, a
 * description in the middle, and the required properties gathered in one
 * subtle toolbar box above the actions. Creates a workflow with a single
 * staff step named after the title.
 */
export function NewWorkDialog({ open, onClose, onCreated }: NewWorkDialogProps) {
  const form = useNewWorkForm({ open, onClose, onCreated });

  // Conditionally rendered so state fully resets between opens.
  if (!open) {
    return null;
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0, pr: 1 }}>
          <Chip size="small" variant="outlined" label="New work" sx={{ alignSelf: "flex-start" }} />
          <InputBase
            autoFocus
            fullWidth
            placeholder="What needs to get done?"
            value={form.title}
            onChange={(e) => form.setTitle(e.target.value)}
            sx={{ fontSize: "1.35rem", fontWeight: 600, lineHeight: 1.3 }}
            inputProps={{ "aria-label": "Work item title" }}
          />
        </Stack>
      }
      primaryActionButtonProps={{
        onClick: form.handleCreate,
        disabled: !form.canSubmit || form.creating,
        loading: form.creating,
        loadingText: "Creating…",
        saveText: "Create work",
        startIcon: null,
      }}
    >
      <Stack spacing={2.5}>
        <InputBase
          fullWidth
          multiline
          minRows={2}
          maxRows={8}
          placeholder="Add a description…"
          value={form.description}
          onChange={(e) => form.setDescription(e.target.value)}
          sx={{ color: "text.secondary", fontSize: "0.9rem" }}
          inputProps={{ "aria-label": "Work item description" }}
        />

        <Divider />

        <WorkPropertiesBar
          tenant={form.tenant}
          setTenant={form.setTenant}
          setTenantSearch={form.setTenantSearch}
          programArea={form.programArea}
          setProgramArea={form.setProgramArea}
          dueAt={form.dueAt}
          setDueAt={form.setDueAt}
          dueType={form.dueType}
          setDueType={form.setDueType}
          tenantOptions={form.tenantOptions}
          tenantsLoading={form.tenantsLoading}
        />
      </Stack>
    </Dialog>
  );
}
